import React from 'react';
import { Education } from '../../../../../utils/types/JobOpenings';

interface IEducationTab {
  educations?: Education[];
}

interface GroupedEducation {
  university: string;
  degrees: Education[];
}

export const EducationTab = (props: IEducationTab) => {
  const { educations } = props;

  const formatDate = (timestamp: number): string | null => {
    if (!timestamp) return null;
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
    });
  };

  // Group education by university
  const groupEducationByUniversity = (): GroupedEducation[] => {
    if (!educations || educations.length === 0) return [];

    const grouped = educations.reduce((acc, edu) => {
      const university = edu.university || 'Unknown University';

      if (!acc[university]) {
        acc[university] = [];
      }
      acc[university].push(edu);
      return acc;
    }, {} as Record<string, Education[]>);

    // Convert to array and sort by graduation year (most recent first)
    return Object.entries(grouped).map(([university, degrees]) => {
      const sortedDegrees = degrees.sort((a, b) => (b.graduationYear || 0) - (a.graduationYear || 0));

      return {
        university,
        degrees: sortedDegrees,
      };
    }).sort((a, b) => {
      const aLatest = Math.max(...a.degrees.map(d => d.graduationYear || 0));
      const bLatest = Math.max(...b.degrees.map(d => d.graduationYear || 0));
      return bLatest - aLatest;
    });
  };

  const groupedEducation = groupEducationByUniversity();

  return (
    <div className='animate-fadeIn py-2'>
      <div className='mb-4  border-b border-gray-200'>
        <h3 className='text-sm font-bold text-gray-900 mb-1'>Education</h3>
      </div>

      { groupedEducation.length > 0 ? (
        <div className='space-y-6'>
          { groupedEducation.map((group, groupIndex) => (
            <div key={groupIndex} className='relative'>
              { /* University-level timeline dot and line */ }
              <div className='absolute left-0 top-1'>
                <div className='w-2 h-2 bg-purple-600 rounded-full'></div>
                { groupIndex !== groupedEducation.length - 1 && (
                  <div
                      className='absolute left-1/2 top-2 -translate-x-1/2 w-0.5 bg-gray-300'
                      style={{ height: 'calc(100% + 24px)' }}
                  ></div>
                ) }
              </div>

              { /* University content */ }
              <div className='pl-8'>
                { /* University header */ }
                <div className='mb-3'>
                  <h4 className='text-[13px] font-bold text-gray-900 leading-tight mb-1'>
                    { group.university }
                  </h4>
                </div>

                { /* Degrees within the university */ }
                <div className='space-y-4 border-l-2 border-gray-200 ml-1'>
                  { group.degrees.map((degree, degreeIndex) => (
                    <div key={degreeIndex} className='relative pl-6'>
                      { /* Degree timeline dot */ }
                      <div className='absolute left-0 top-1 -translate-x-1/2'>
                        <div className='w-1.5 h-1.5 bg-gray-400 rounded-full'></div>
                      </div>

                      { /* Degree content */ }
                      <div>
                        <h5 className='text-[13px] font-semibold text-gray-800 leading-tight mb-1'>
                          { degree.degree }
                        </h5>

                        { degree.fieldOfStudy && (
                          <p className='text-xs text-gray-600 mb-2'>
                            { degree.fieldOfStudy }
                          </p>
                        ) }

                        <p className='text-xs text-gray-500'>
                          Graduated: { formatDate(Number(degree.graduationYear)) }
                        </p>
                      </div>
                    </div>
                  )) }
                </div>
              </div>
            </div>
          )) }
        </div>
      ) : (
        <div className='text-center py-8'>
          <svg
              className='w-12 h-12 text-gray-300 mx-auto mb-3'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
            />
          </svg>
          <p className='text-sm text-gray-500'>No education available</p>
        </div>
      ) }
    </div>
  );
};
